import { X } from "lucide-react";
import React, { useEffect } from "react";

interface BottomSheetProps {
  children: React.ReactNode;
  show: boolean;
  showCloseButton?: boolean;
  title?: string;
  header?: React.ReactNode;
  handleClose: () => void;
  /** Optional z-index for overlay (e.g. 60 to appear above connection flow) */
  zIndex?: number;
  height?: number; // in percentage
  fullScreen?: boolean;
}

const BottomSheet: React.FC<BottomSheetProps> = ({
  children,
  show = false,
  showCloseButton = false,
  title = "",
  header = null,
  handleClose,
  zIndex = 40,
  height = 0,
  fullScreen = false,
}) => {
  // Lock body scroll only while the sheet is actually visible
  useEffect(() => {
    if (!show) return;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [show]);

  if (!show) return null;

  return (
    <div
      className="fixed top-0 bottom-0 left-0 right-0 bg-black/50 max-w-lg mx-auto"
      style={{ zIndex }}
      onClick={(e) => {
        e.preventDefault();
        handleClose();
      }}
    >
      <div
        className={`fixed bottom-0 left-0 right-0 z-10 overflow-hidden overflow-y-auto max-w-lg mx-auto ${
          !fullScreen
            ? "border-t border-gray-200 rounded-t-2xl"
            : "border-none rounded-none"
        }`}
        onClick={(e) => {
          e.stopPropagation();
        }}
        style={{ height: fullScreen ? "100%" : height ? `${height}%` : "auto" }}
      >
        {showCloseButton && (
          <button
            className="absolute top-2 right-2 p-2 hover:bg-gray-100 rounded-full z-10"
            onClick={(e) => {
              e.preventDefault();
              handleClose();
            }}
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        )}
        <div className="bg-white h-full overflow-hidden">
          {header && <div>{header}</div>}
          {title && (
            <div className="flex justify-between items-center px-4 py-3 border-b border-gray-200">
              <h1 className="text-lg font-semibold">{title}</h1>
            </div>
          )}
          <div
            className={`${
              fullScreen ? "h-full" : "max-h-[calc(100vh-200px)]"
            } overflow-y-auto overflow-x-hidden`}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BottomSheet;
